import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  doc,
  updateDoc,
  deleteDoc,
  getDoc,
  limit,
} from 'firebase/firestore';
import { db } from '../firebase';
import { maintenanceService } from '../Services/maintenanceService';
import { getAuth } from 'firebase/auth';
import AdminLayout from '../components/AdminLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Users,
  FileText,
  MessageSquare,
  Flag,
  CheckCircle,
  XCircle,
  Eye,
  Calendar,
  ArrowRight,
  Loader2,
  PlusCircle,
  BarChart3,
  AlertTriangle,
  Clock,
  Shield,
  Trash2,
  Ban,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  UserCheck,
  UserX,
  ExternalLink,
  Mail,
} from 'lucide-react';

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedReport, setExpandedReport] = useState(null);
  const [maintenanceStatus, setMaintenanceStatus] = useState({ aiServicesEnabled: true });
  const [maintenanceReason, setMaintenanceReason] = useState('');

  const [stats, setStats] = useState({
    totalUsers: 0,
    verifiedUsers: 0,
    recentUsers: [],
    totalArticles: 0,
    publishedArticles: 0,
    totalThreads: 0,
    totalReplies: 0,
    pendingReports: [],
    resolvedReports: 0,
    pendingFeedbacks: [],
    totalFeedbacks: 0,
  });

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    const unsubscribe = maintenanceService.subscribeToMaintenanceStatus((status) => {
      setMaintenanceStatus(status);
    });
    return () => unsubscribe();
  }, []);

  const loadStats = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      // UTILISATEURS
      const usersSnap = await getDocs(collection(db, 'users'));
      const allUsers = usersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const verifiedUsers = allUsers.filter((u) => u.emailVerified !== false).length;
      const recentUsers = [...allUsers]
        .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0))
        .slice(0, 5);

      // ARTICLES
      const articlesSnap = await getDocs(collection(db, 'articles'));
      const publishedArticles = articlesSnap.docs.filter((d) => d.data().published).length;

      // FORUM
      const threadsSnap = await getDocs(collection(db, 'threads'));
      const repliesSnap = await getDocs(collection(db, 'replies'));

      // SIGNALEMENTS EN ATTENTE
      const reportsSnap = await getDocs(
        query(collection(db, 'reports'), where('status', '==', 'pending'))
      );
      const pendingReports = await Promise.all(
        reportsSnap.docs.map(async (d) => {
          const report = { id: d.id, ...d.data() };
          // Charger le contenu signalé
          try {
            const collectionName = report.type === 'thread' ? 'threads' : 'replies';
            const itemDoc = await getDoc(doc(db, collectionName, report.itemId));
            report.itemData = itemDoc.exists() ? itemDoc.data() : null;
            // Charger le profil de l'auteur du signalement
            const reporterDoc = await getDoc(doc(db, 'users', report.reportedBy));
            report.reporterData = reporterDoc.exists() ? reporterDoc.data() : null;
          } catch (e) {}
          return report;
        })
      );

      // SIGNALEMENTS RÉSOLUS
      const resolvedSnap = await getDocs(
        query(collection(db, 'reports'), where('status', '!=', 'pending'))
      );

      // FEEDBACKS
      const feedbacksSnap = await getDocs(
        query(collection(db, 'feedbacks'), where('status', '==', 'nouveau'))
      );
      const allFeedbacksSnap = await getDocs(collection(db, 'feedbacks'));
      const pendingFeedbacks = feedbacksSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));

      setStats({
        totalUsers: allUsers.length,
        verifiedUsers,
        recentUsers,
        totalArticles: articlesSnap.size,
        publishedArticles,
        totalThreads: threadsSnap.size,
        totalReplies: repliesSnap.size,
        pendingReports: pendingReports.sort(
          (a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)
        ),
        resolvedReports: resolvedSnap.size,
        pendingFeedbacks,
        totalFeedbacks: allFeedbacksSnap.size,
      });
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const toggleMaintenanceMode = async () => {
    const newStatus = !maintenanceStatus.aiServicesEnabled;
    const reason = newStatus ? '' : (maintenanceReason || "Maintenance d'urgence");
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      alert('❌ Erreur : utilisateur non connecté');
      return;
    }

    const confirmMsg = newStatus
      ? 'DÉSACTIVER le mode maintenance ?\n\nLes services IA seront rétablis pour tous les utilisateurs.'
      : 'ACTIVER le mode maintenance ?\n\nLes services IA seront bloqués pour tous les utilisateurs (sauf admins).';

    if (window.confirm(confirmMsg)) {
      try {
        await maintenanceService.setMaintenanceMode(newStatus, reason, user.uid);
        alert(`✅ Mode maintenance ${newStatus ? 'désactivé' : 'activé'}`);
        setMaintenanceReason('');
      } catch (error) {
        alert('❌ Erreur : ' + error.message);
      }
    }
  };

  // IGNORER un signalement
  const handleIgnoreReport = async (reportId) => {
    try {
      await updateDoc(doc(db, 'reports', reportId), {
        status: 'ignored',
        resolvedAt: new Date(),
      });
      setStats((prev) => ({
        ...prev,
        pendingReports: prev.pendingReports.filter((r) => r.id !== reportId),
        resolvedReports: prev.resolvedReports + 1,
      }));
    } catch (e) {
      console.error('Erreur:', e);
    }
  };

  // SUPPRIMER le contenu signalé
  const handleDeleteContent = async (report) => {
    try {
      const collectionName = report.type === 'thread' ? 'threads' : 'replies';
      await deleteDoc(doc(db, collectionName, report.itemId));
      await updateDoc(doc(db, 'reports', report.id), {
        status: 'resolved',
        resolvedAt: new Date(),
      });
      setStats((prev) => ({
        ...prev,
        pendingReports: prev.pendingReports.filter((r) => r.id !== report.id),
        resolvedReports: prev.resolvedReports + 1,
        totalThreads: report.type === 'thread' ? prev.totalThreads - 1 : prev.totalThreads,
        totalReplies: report.type === 'reply' ? prev.totalReplies - 1 : prev.totalReplies,
      }));
    } catch (e) {
      console.error('Erreur suppression:', e);
    }
  };

  // MARQUER feedback comme traité
  const handleMarkFeedbackDone = async (feedbackId) => {
    try {
      await updateDoc(doc(db, 'feedbacks', feedbackId), {
        status: 'traité',
        resolvedAt: new Date(),
      });
      setStats((prev) => ({
        ...prev,
        pendingFeedbacks: prev.pendingFeedbacks.filter((f) => f.id !== feedbackId),
      }));
    } catch (e) {
      console.error('Erreur:', e);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate?.() || new Date(timestamp);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate?.() || new Date(timestamp);
    const now = new Date();
    const diff = Math.floor((now - date) / 60000);
    if (diff < 60) return `Il y a ${diff}min`;
    if (diff < 1440) return `Il y a ${Math.floor(diff / 60)}h`;
    return `Il y a ${Math.floor(diff / 1440)}j`;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-red-500" />
        </div>
      </AdminLayout>
    );
  }

  const statsCards = [
    {
      title: 'Utilisateurs',
      value: stats.totalUsers,
      sub: `${stats.verifiedUsers} vérifiés`,
      icon: Users,
      color: 'from-blue-500 to-cyan-500',
      bg: 'from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30',
    },
    {
      title: 'Signalements',
      value: stats.pendingReports.length,
      sub: `${stats.resolvedReports} résolus`,
      icon: Flag,
      color:
        stats.pendingReports.length > 0
          ? 'from-red-500 to-orange-500'
          : 'from-green-500 to-emerald-500',
      bg:
        stats.pendingReports.length > 0
          ? 'from-red-50 to-orange-50 dark:from-red-950/30 dark:to-orange-950/30'
          : 'from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30',
      urgent: stats.pendingReports.length > 0,
    },
    {
      title: 'Forum',
      value: stats.totalThreads,
      sub: `${stats.totalReplies} réponses`,
      icon: MessageSquare,
      color: 'from-purple-500 to-pink-500',
      bg: 'from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30',
    },
    {
      title: 'Articles',
      value: stats.publishedArticles,
      sub: `${stats.totalArticles} au total`,
      icon: FileText,
      color: 'from-orange-500 to-amber-500',
      bg: 'from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/30',
    },
    {
      title: 'Feedbacks',
      value: stats.pendingFeedbacks.length,
      sub: `${stats.totalFeedbacks} au total`,
      icon: Mail,
      color: 'from-teal-500 to-green-500',
      bg: 'from-teal-50 to-green-50 dark:from-teal-950/30 dark:to-green-950/30',
      urgent: stats.pendingFeedbacks.length > 0,
    },
  ];

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">
              Dashboard Admin
            </h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm">Vue d'ensemble et modération</p>
          </div>
          <Button
            onClick={() => loadStats(true)}
            variant="outline"
            size="sm"
            disabled={refreshing}
            className="dark:border-gray-700"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>

        {/* ═══════════════════════════════════════ */}
        {/* CONTRÔLE DE MAINTENANCE - PRIORITAIRE */}
        {/* ═══════════════════════════════════════ */}
        <Card className={`border-2 p-5 mb-6 ${
          maintenanceStatus.aiServicesEnabled
            ? 'bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 border-green-300 dark:border-green-700'
            : 'bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-950/30 dark:to-orange-950/30 border-red-400 dark:border-red-600'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                maintenanceStatus.aiServicesEnabled
                  ? 'bg-gradient-to-br from-green-500 to-emerald-500'
                  : 'bg-gradient-to-br from-red-500 to-orange-500'
              }`}>
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                    {maintenanceStatus.aiServicesEnabled ? '🟢 Services IA Actifs' : '🔴 MODE MAINTENANCE'}
                  </h3>
                  {!maintenanceStatus.aiServicesEnabled && (
                    <span className="px-2 py-1 bg-red-600 text-white text-xs font-bold rounded-full animate-pulse">
                      ACTIF
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {maintenanceStatus.aiServicesEnabled
                    ? 'Chat IA et analyse de sang disponibles pour tous'
                    : 'Services IA bloqués pour les utilisateurs (admins autorisés)'}
                </p>
                {!maintenanceStatus.aiServicesEnabled && maintenanceStatus.reason && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">
                    Raison : {maintenanceStatus.reason}
                  </p>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-2 items-end">
              {!maintenanceStatus.aiServicesEnabled && (
                <input
                  type="text"
                  placeholder="Raison (optionnel)"
                  value={maintenanceReason}
                  onChange={(e) => setMaintenanceReason(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 w-64"
                />
              )}
              <Button
                onClick={toggleMaintenanceMode}
                className={`font-bold px-6 py-2 ${
                  maintenanceStatus.aiServicesEnabled
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-green-600 hover:bg-green-700'
                } text-white`}
              >
                {maintenanceStatus.aiServicesEnabled ? (
                  <>
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    ACTIVER MAINTENANCE
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    DÉSACTIVER MAINTENANCE
                  </>
                )}
              </Button>
            </div>
          </div>
        </Card>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          {statsCards.map((stat, idx) => (
            <Card
              key={idx}
              className={`bg-gradient-to-br ${stat.bg} border-2 border-gray-200 dark:border-gray-800 p-5 relative overflow-hidden`}
            >
              {stat.urgent && (
                <span className="absolute top-2 right-2 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
              )}
              <div
                className={`w-10 h-10 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center mb-3 shadow`}
              >
                <stat.icon className="w-5 h-5 text-white" />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{stat.title}</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{stat.value}</p>
              <p className="text-xs text-gray-400 mt-1">{stat.sub}</p>
            </Card>
          ))}
        </div>

        {/* ═══════════════════════════════════════ */}
        {/* SECTION SIGNALEMENTS */}
        {/* ═══════════════════════════════════════ */}
        <Card className="bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 p-6 mb-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl flex items-center justify-center">
                <Flag className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  Signalements en attente
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Contenu signalé par les utilisateurs
                </p>
              </div>
              {stats.pendingReports.length > 0 && (
                <span className="px-2.5 py-1 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-xs font-bold rounded-full">
                  {stats.pendingReports.length} en attente
                </span>
              )}
            </div>
          </div>

          {stats.pendingReports.length === 0 ? (
            <div className="text-center py-10">
              <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400 font-medium">
                Aucun signalement en attente
              </p>
              <p className="text-gray-400 text-sm mt-1">La communauté est au calme !</p>
            </div>
          ) : (
            <div className="space-y-3">
              {stats.pendingReports.map((report) => (
                <div
                  key={report.id}
                  className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden"
                >
                  {/* Header du signalement */}
                  <div
                    className="flex items-start justify-between p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50"
                    onClick={() =>
                      setExpandedReport(expandedReport === report.id ? null : report.id)
                    }
                  >
                    <div className="flex items-start gap-3 flex-1">
                      <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          report.type === 'thread'
                            ? 'bg-purple-100 dark:bg-purple-900/40'
                            : 'bg-blue-100 dark:bg-blue-900/40'
                        }`}
                      >
                        {report.type === 'thread' ? (
                          <MessageSquare className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                        ) : (
                          <MessageSquare className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              report.type === 'thread'
                                ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300'
                                : 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                            }`}
                          >
                            {report.type === 'thread' ? 'Thread' : 'Réponse'}
                          </span>
                          <span className="text-xs px-2 py-0.5 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded-full font-medium">
                            {report.reason}
                          </span>
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatTime(report.createdAt)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-300 truncate">
                          {report.itemData?.title ||
                            report.itemData?.content?.substring(0, 80) ||
                            'Contenu supprimé'}
                          {!report.itemData?.title && report.itemData?.content?.length > 80
                            ? '...'
                            : ''}
                        </p>
                        {report.details && (
                          <p className="text-xs text-gray-400 mt-1 italic">"{report.details}"</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      {/* Actions rapides */}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleIgnoreReport(report.id);
                        }}
                        className="text-xs border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <XCircle className="w-3.5 h-3.5 mr-1 text-gray-500" />
                        Ignorer
                      </Button>
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteContent(report);
                        }}
                        className="text-xs bg-red-500 hover:bg-red-600 text-white"
                        disabled={!report.itemData}
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1" />
                        Supprimer
                      </Button>
                      {expandedReport === report.id ? (
                        <ChevronUp className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Contenu expandé */}
                  {expandedReport === report.id && (
                    <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-800/50">
                      <div className="grid md:grid-cols-2 gap-4">
                        {/* Contenu signalé */}
                        <div>
                          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
                            Contenu signalé
                          </p>
                          {report.itemData ? (
                            <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                              {report.itemData.title && (
                                <p className="font-semibold text-sm text-gray-900 dark:text-gray-100 mb-1">
                                  {report.itemData.title}
                                </p>
                              )}
                              <p className="text-sm text-gray-600 dark:text-gray-300">
                                {report.itemData.content}
                              </p>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-400 italic">Contenu déjà supprimé</p>
                          )}
                        </div>

                        {/* Signalé par */}
                        <div>
                          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
                            Signalé par
                          </p>
                          <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {report.reporterData?.first_name} {report.reporterData?.last_name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {report.reporterData?.email}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              Signalé le {formatDate(report.createdAt)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* ═══════════════════════════════════════ */}
        {/* SECTION FEEDBACKS */}
        {/* ═══════════════════════════════════════ */}
        <Card className="bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 p-6 mb-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-green-500 rounded-xl flex items-center justify-center">
                <Mail className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  Feedbacks utilisateurs
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">Retours et suggestions</p>
              </div>
              {stats.pendingFeedbacks.length > 0 && (
                <span className="px-2.5 py-1 bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 text-xs font-bold rounded-full">
                  {stats.pendingFeedbacks.length} nouveaux
                </span>
              )}
            </div>
          </div>

          {stats.pendingFeedbacks.length === 0 ? (
            <div className="text-center py-10">
              <Mail className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400 font-medium">
                Aucun feedback en attente
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {stats.pendingFeedbacks.map((feedback) => (
                <div
                  key={feedback.id}
                  className="border border-gray-200 dark:border-gray-700 rounded-xl p-4"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs px-2 py-1 rounded-full font-medium ${
                          feedback.subject === 'bug'
                            ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                            : feedback.subject === 'suggestion'
                              ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                              : feedback.subject === 'question'
                                ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300'
                                : feedback.subject === 'ia'
                                  ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300'
                                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {feedback.subject === 'bug' && '🐛 Bug'}
                        {feedback.subject === 'suggestion' && '💡 Suggestion'}
                        {feedback.subject === 'question' && '❓ Question'}
                        {feedback.subject === 'ia' && '🤖 Comportement IA'}
                        {feedback.subject === 'autre' && '📝 Autre'}
                      </span>
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatTime(feedback.createdAt)}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleMarkFeedbackDone(feedback.id)}
                        className="text-xs"
                      >
                        <CheckCircle className="w-3.5 h-3.5 mr-1" />
                        Traité
                      </Button>
                    </div>
                  </div>

                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-2 whitespace-pre-wrap">
                    {feedback.message}
                  </p>

                  <div className="flex items-center gap-2 text-xs text-gray-400 pt-2 border-t border-gray-100 dark:border-gray-800">
                    <span>Par: {feedback.email}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* ═══════════════════════════════════════ */}
        {/* SECTION UTILISATEURS + FORUM */}
        {/* ═══════════════════════════════════════ */}
        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          {/* Derniers inscrits */}
          <Card className="bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-500" />
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  Derniers inscrits
                </h2>
              </div>
              <Button
                onClick={() => navigate('/admin/users')}
                variant="outline"
                size="sm"
                className="dark:border-gray-700 dark:text-gray-300 text-xs"
              >
                Voir tout
                <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>

            <div className="space-y-2">
              {stats.recentUsers.length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-6">Aucun utilisateur</p>
              ) : (
                stats.recentUsers.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                      {user.first_name?.[0]}
                      {user.last_name?.[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {user.first_name} {user.last_name}
                      </p>
                      <p className="text-xs text-gray-400 truncate">{user.email}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {user.emailVerified ? (
                        <UserCheck className="w-4 h-4 text-green-500" title="Email vérifié" />
                      ) : (
                        <UserX className="w-4 h-4 text-orange-400" title="Email non vérifié" />
                      )}
                      <span className="text-xs text-gray-400">{formatDate(user.createdAt)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>

          {/* Actions rapides */}
          <Card className="bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-5 h-5 text-red-500" />
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                Actions rapides
              </h2>
            </div>

            <div className="space-y-2">
              {[
                {
                  label: 'Créer un article',
                  desc: 'Rédiger un nouveau guide médical',
                  icon: PlusCircle,
                  color: 'text-blue-500',
                  action: () => navigate('/admin/articles'),
                },
                {
                  label: 'Gérer les utilisateurs',
                  desc: `${stats.totalUsers} comptes enregistrés`,
                  icon: Users,
                  color: 'text-green-500',
                  action: () => navigate('/admin/users'),
                },
                {
                  label: 'Gérer les articles',
                  desc: `${stats.publishedArticles} publiés / ${stats.totalArticles - stats.publishedArticles} brouillons`,
                  icon: FileText,
                  color: 'text-orange-500',
                  action: () => navigate('/admin/articles'),
                },
                {
                  label: 'Google Analytics',
                  desc: 'Statistiques avancées',
                  icon: BarChart3,
                  color: 'text-purple-500',
                  action: () => window.open('https://analytics.google.com', '_blank'),
                },
              ].map((item, idx) => (
                <button
                  key={idx}
                  onClick={item.action}
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left group"
                >
                  <item.icon className={`w-5 h-5 ${item.color} flex-shrink-0`} />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {item.label}
                    </p>
                    <p className="text-xs text-gray-400">{item.desc}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 group-hover:translate-x-0.5 transition-all" />
                </button>
              ))}
            </div>
          </Card>
        </div>

        {/* ═══════════════════════════════════════ */}
        {/* STATS FORUM */}
        {/* ═══════════════════════════════════════ */}
        <Card className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30 border-2 border-purple-200 dark:border-purple-800 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  Activité du forum
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {stats.totalThreads} discussions · {stats.totalReplies} réponses au total
                </p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/forum')}
              variant="outline"
              size="sm"
              className="dark:border-purple-700 dark:text-purple-300 border-purple-300"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Voir le forum
            </Button>
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboardPage;
